import React, { useMemo, memo } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme as osTheme } from '../../constants/theme';
import MuiDrawer from '@mui/material/Drawer';
import { styled } from '@mui/material/styles';
import CloseIcon from '../CustomSVGIcons/CloseIcon';

// ReusableDrawer Component
export const ReusableDrawer: React.FC<{
    open: boolean;
    onClose: () => void;
    anchor: 'left' | 'right' | 'top' | 'bottom';
    content: React.ReactNode;
}> = memo(({ open, onClose, anchor, content }) => {
    const { themeStyle } = useThemeContext();
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const drawerWidthDesktop = '70%';
    const drawerWidthMobile = '100%';

    // Memoize the styled component to prevent recreating it on every render
    const StyledDrawer = useMemo(
        () =>
            styled(MuiDrawer)(({ theme }) => ({
                '.MuiDrawer-paper': {
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    width: isMobile ? drawerWidthMobile : drawerWidthDesktop,
                    backgroundColor: osTheme[themeStyle].pure_black,
                    borderLeft: `0.1px solid ${osTheme[themeStyle].tag_white_light}`,
                    transform: open ? 'translateX(0)' : 'translateX(100%)',
                    transition: theme.transitions.create('transform', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen * 2,
                    }),
                    zIndex: 9999,
                    overflowY: 'auto',
                },
                '.MuiBackdrop-root': {
                    backgroundColor: open && !isMobile ? 'rgba(0, 0, 0, 0.5)' : 'transparent',
                },
            })),
        [themeStyle, isMobile, open],
    );

    return (
        <StyledDrawer
            disableEnforceFocus
            open={open}
            anchor={anchor}
            onClose={onClose}
            ModalProps={{
                keepMounted: true,
                // Add this to optimize rendering
                BackdropProps: {
                    transitionDuration: 0,
                },
            }}>
            <CloseIcon
                onClick={onClose}
                style={{ position: 'absolute', left: 10, top: 10, cursor: 'pointer', zIndex: 9999 }}
                stroke={osTheme[themeStyle].tag_white}
            />
            {content}
        </StyledDrawer>
    );
});
